import Papa, { type ParseResult } from "papaparse";

import { type Decision, isValidDecision, VALID_DECISIONS } from "~/features/compoundRecordComposers/types-upload";

export interface CompoundRecordComposerCsvRow {
  mainCandidateId: string;
  compoundCandidateId: string;
  decision: Decision;
  memo?: string;
}

type CsvRow = Record<string, string>;

function parseCsvRows(content: string): ParseResult<CsvRow> {
  return Papa.parse<CsvRow>(content, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
    transform: (value) => value.trim(),
  });
}

function validateColumns(headers: string[]): void {
  const requiredColumns = ["main_candidate_ID", "compound_candidate_ID", "decision"];
  const missingColumns = requiredColumns.filter((col) => !headers.includes(col));
  if (missingColumns.length > 0) {
    throw new Error(`Missing required columns: ${missingColumns.join(", ")}`);
  }
}

function validateRow(row: CsvRow, index: number): void {
  const mainCandidateId = row["main_candidate_ID"];
  const compoundCandidateId = row["compound_candidate_ID"];
  const decision = row.decision;

  if (!mainCandidateId || !compoundCandidateId || !decision) {
    throw new Error(`Row ${index + 2}: Required fields are empty`);
  }

  if (!isValidDecision(decision)) {
    throw new Error(`Row ${index + 2}: decision value must be one of ${VALID_DECISIONS.join(", ")}`);
  }
}

function toCompoundRecordComposerCsvRows(data: CsvRow[]): CompoundRecordComposerCsvRow[] {
  return data.map((row) => {
    const decision = row.decision;
    if (!isValidDecision(decision)) {
      throw new Error(`Invalid decision value: ${decision}`);
    }
    return {
      mainCandidateId: row["main_candidate_ID"],
      compoundCandidateId: row["compound_candidate_ID"],
      decision,
      memo: row.memo,
    };
  });
}

/**
 * Parse CSV content and convert to CompoundRecordComposerCsvRow[]
 * @param content CSV file content as string
 * @returns Array of parsed CSV rows
 * @throws {Error} If CSV parsing fails, required columns are missing, or validation fails
 */
export function parseCompoundRecordComposerCsv(content: string): CompoundRecordComposerCsvRow[] {
  const result = parseCsvRows(content);

  if (result.errors.length > 0) {
    throw new Error(`CSV parsing error: ${result.errors[0].message}`);
  }

  if (result.data.length === 0) {
    throw new Error("CSV file must contain data rows");
  }

  const headers = Object.keys(result.data[0]);
  validateColumns(headers);

  result.data.forEach((row, index) => {
    validateRow(row, index);
  });

  return toCompoundRecordComposerCsvRows(result.data);
}
